import { adsAssistantClientFetch, SOCIAL_POST_MEDIA_BASE } from "./adsAssistantClientHttp";
import { requireSupabase } from "./supabase";

export type AdsSocialPostMediaSignOk = {
  ok: true;
  bucket: string;
  path: string;
  token: string;
  signed_url: string;
  media_id: string;
  content_type: string;
};

export type AdsSocialPostMediaCommitOk = {
  ok: true;
  media: { id: string; object_path: string; sort_order: number; created_at: string };
};

function isSignOk(x: unknown): x is AdsSocialPostMediaSignOk {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return (
    o.ok === true &&
    typeof o.bucket === "string" &&
    typeof o.path === "string" &&
    typeof o.token === "string" &&
    typeof o.media_id === "string" &&
    typeof o.content_type === "string"
  );
}

function isCommitOk(x: unknown): x is AdsSocialPostMediaCommitOk {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return o.ok === true && o.media !== null && typeof o.media === "object";
}

/** After a post is saved, upload pending images (sign → Storage PUT → commit). Returns an error message or null on full success. */
export async function uploadManualAdPostImages(postId: string, files: File[]): Promise<string | null> {
  if (!files.length) return null;
  const sb = requireSupabase();

  for (const file of files) {
    const ct = (file.type || "image/jpeg").toLowerCase();
    const sign = await adsAssistantClientFetch<unknown>(SOCIAL_POST_MEDIA_BASE, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        op: "sign",
        social_post_id: postId,
        content_type: ct,
        byte_size: file.size,
      }),
    });

    if (!sign.ok) return sign.error;
    if (!isSignOk(sign.data)) {
      return typeof (sign.data as { error?: string })?.error === "string"
        ? (sign.data as { error: string }).error
        : "Sign response invalid";
    }
    const s = sign.data;

    const { error: upErr } = await sb.storage.from(s.bucket).uploadToSignedUrl(s.path, s.token, file, {
      contentType: s.content_type,
      upsert: false,
    });
    if (upErr) return upErr.message;

    const commit = await adsAssistantClientFetch<unknown>(SOCIAL_POST_MEDIA_BASE, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        op: "commit",
        social_post_id: postId,
        media_id: s.media_id,
        object_path: s.path,
        content_type: s.content_type,
        byte_size: file.size,
      }),
    });
    if (!commit.ok) return commit.error;
    if (!isCommitOk(commit.data)) {
      return typeof (commit.data as { error?: string })?.error === "string"
        ? (commit.data as { error: string }).error
        : "Commit response invalid";
    }
  }

  return null;
}

/** Remove one `ads_assistant_social_post_media` row (Storage first, then DB). Same ingest gate as sign/commit. */
export async function deleteAdsSocialPostMedia(
  mediaId: string
): Promise<{ ok: true } | { ok: false; error: string }> {
  const url = `${SOCIAL_POST_MEDIA_BASE}?id=${encodeURIComponent(mediaId)}`;
  const r = await adsAssistantClientFetch<{ ok?: boolean; error?: string }>(url, { method: "DELETE" });
  if (!r.ok) return { ok: false, error: r.error };
  return { ok: true };
}
